import type { Media } from './media';

/**
 * `client` is whoever commissioned the work (often an agency acting for an end
 * client); `collaborator` is a studio or artist worked alongside; `press` is
 * third-party coverage.
 */
export type ProjectLink = {
  readonly kind: 'live' | 'source' | 'write-up' | 'client' | 'collaborator' | 'press';
  readonly url: string;
  readonly label?: string;
};

export type Project = {
  readonly slug: string;
  readonly title: string;
  readonly client: string;
  readonly year: string;
  /** One line. Rail tooltips, meta tags, open-graph — never multi-paragraph. */
  readonly summary: string;
  /**
   * Detail-pane prose; one array element per rendered paragraph. Interpolated
   * as raw HTML, so inline markup (links, `<em>`) works — and so content is
   * trusted input. Same contract as `homeBlurbs` in `copy.ts`.
   */
  readonly description: readonly string[];
  /**
   * Filename of a small rail-sized derivative in the project folder. Falls back
   * to `media[0]` when absent, which ships the full-resolution original into a
   * ~112px square — see the media budget in CLAUDE.md.
   */
  readonly thumbnail?: string;
  readonly media: readonly Media[];
  readonly links?: readonly ProjectLink[];
};

export const projects: readonly Project[] = [
  {
    slug: 'google-cloud-2026',
    title: 'Google Cloud 2026',
    client: 'George P. Johnson Agency on behalf of Google',
    year: "2026",
    summary:
      'A block-stacking game for the Google Cloud Conference 2026, where attendee scores accumulated toward opening a shared prize vault.',
    description: [
      'In collaboration with GPJ we built a block-stacking game that ran during the Google Cloud Conference in 2026. Attendees would play throughout the event contributing to a shared cumulative score which, once enough players had participated, would open a physical vault that contained a special prize.',
      'In addition to the cumulative prize, individual players could earn swag based on their personal score.',
      'Lead Programmer: Jason Araujo (IEDLabs, Vermilion)',
      'Lead Designer: Max Glascott (Vermilion)',
      'Built in Unity3D',
    ],
    thumbnail: 'thumb.jpg',
    media: [
      {
        kind: 'local-image',
        filename: 'google_cloud_2026_00.png',
        alt: 'Google Cloud 2026',
        width: 1649,
        height: 984,
      },
    ],
    links: [
      { kind: 'client', label: 'GPJ', url: 'https://gpj.com' },
      { kind: 'collaborator', label: 'VRMLN', url: 'https://vrmln.co' },
    ],
  },
  {
    slug: 'mit-museum',
    title: 'MIT Museum: The Window',
    client: 'BlueCadet Agency on behalf of the MIT Museum',
    year: "2022",
    summary:
      'The Window is a playful, generative data visualization of the MIT community, showcasing their radical ideas and innovations.',
    description: [
      'The Window visualizes the MIT community allowing users to complete a survey which is then used to procedurally generate a 3D avatar which wiggles, bounces, and bobs on screen.',
      'A custom GPU-based workflow was developed to generate and display hundreds of procedurally generated avatars simultaneously in real-time.',
      'Lead Developer: Jason Araujo (IEDLabs)',
      'Built in Unity3D',
    ],
    thumbnail: 'thumb.jpg',
    media: [
      {
        kind: 'local-image',
        filename: 'mit_museum_00.png',
        alt: 'MIT Museum: The Window',
        width: 877,
        height: 560,
      },
      {
        kind: 'vimeo',
        id: '762921595',
        title: 'The Window: video courtesy of BlueCadet',
        aspectRatio: '16/9',
      },
    ],
    links: [
      { kind: 'press', label: 'The MIT Museum', url: 'https://mitmuseum.mit.edu/the-exchange/stories/a-window-into-mit' },
      { kind: 'client', label: 'BlueCadet', url: 'https://www.bluecadet.com/work/mit-museum' },
      { kind: 'press', label: 'FWA Case Study', url: 'https://thefwa.com/cases/mit-museum-the-window' },
    ],
  },
  {
    slug: 'aftermath',
    title: 'Live Performance at the Aftermath Music Festival',
    client: 'In collaboration with Mend',
    year: "a long long time ago",
    summary: 'Light sculptures on stage synchronized with a virtual world projected on screen, all drive by live music from Mend',
    description: [
      'We created a set of light sculptures that lit the stage in sync with a virtual world built in Unity3D.',
      'Both the light sculptures and the virtual world reacted in real-time to audio from the Mend performance, creating a fully immersive live show.',
      'The great sadness of this project is that it is so poorly documented. We were young and wild, up late until the last minute, and didn\'t put much thought into capturing the output. The show was incredible and will live on in infamy in our minds. What a time it was.',
      'Mend: Max Glascott and Kassi Cork',
      'Visuals: Max Glascott (Vermilion)',
      'Developer: Jason Araujo (IEDLabs)',
      'Built using many Fadecandy LED controllers, a Raspberry Pi, and Unity3D',
    ],
    thumbnail: 'thumb.jpg',
    media: [
      {
        kind: 'local-image',
        filename: 'toronto_stage.jpg',
        alt: 'Mend Live Performance at the Aftermath music festival in Toronto, Canada',
        width: 540,
        height: 361,
      },
      {
        kind: 'local-image',
        filename: 'toronto_stage_2.jpg',
        alt: 'Mend Live Performance at the Aftermath music festival in Toronto, Canada',
        width: 540,
        height: 361,
      },
      {
        kind: 'local-image',
        filename: 'cones_1.jpg',
        alt: 'Mend Live Performance at the Aftermath music festival in Toronto, Canada',
        width: 860,
        height: 645,
      },
      {
        kind: 'local-image',
        filename: 'cones_2.jpg',
        alt: 'Mend Live Performance at the Aftermath music festival in Toronto, Canada',
        width: 860,
        height: 645,
      },
    ],
    links: [
      { kind: 'collaborator', label: 'Mend Music', url: 'https://mendmusic.bandcamp.com/' },
    ],
  },
  {
    slug: 'ampex',
    title: 'Ampex: Audio Reactive Sculpture',
    client: 'Obsydian Studios on behalf of Ampex Research Center at Alexandria',
    year: "2021",
    summary: 'An audio reactive sculpture developed for the Ampex Research Center at Alexandria.',
    description: [
      'This project was devised by Obsydian Studios to commemorate the invention of the vacuum tube, which enabled a wide array of electronic advancements and became a bedrock of the music and recording industry of which Ampex was an essential part.',
      'The sculpture was made with hundreds of glass tubes in honor of the vacuum tube. The sculpture was audio reactive, and would dynamically respond to sound inputs in real-time.',
      'Fabrication and Design: Obsydian Studios',
      'Lead Developer: Jason Araujo (IEDLabs)',
      'Built using a Teensy microcontroller and a Raspberry Pi and over 8000 LEDs',
    ],
    thumbnail: 'thumb.jpg',
    media: [
      {
        kind: 'local-video',
        filename: 'ampex_idle_web.mp4',
        alt: 'Ampex Tube Sculpture: Idle Mode',
        poster: 'ampex_poster.jpg',
        width: 1920,
        height: 1080,
      },
      {
        kind: 'external-link',
        url: 'https://www.instagram.com/p/COesPllCOyF/',
        title: 'behind the scenes',
        thumbnail: 'ampex_2.png',
      },
      {
        kind: 'external-link',
        url: 'https://www.instagram.com/p/CPSaj3_MlP1/?img_index=1',
        title: 'more behind the scenes',
        thumbnail: 'ampex_0.png',
      },
    ],
    links: [
      { kind: 'client', label: 'Obsydian Studios', url: 'https://obsydianstudios.com/experiences' },
    ],
  },
   {
    slug: 'adler',
    title: 'Adler Planetarium - The Universe: A Walk Through Space and Time ',
    client: 'Adler Planetarium',
    year: "2012-present (permanent install)",
    summary:
      'A permanent interactive exhibition at the Adler Planetarium tracing how galaxies, stars, planets and the atoms in our bodies were formed as the Universe grew.',
    description: [
      'The Universe: A Walk through Space and Time is a sleek interactive exhibition installed at the Adler Planetarium in Chicago. Visitors encounter the Universe through the M1 designed and programmed interactives that show audiences how galaxies, stars, planets – and even the very atoms in their bodies – were created as the Universe grew. A spectacular cinematic projection wall showcases the vast enormity of regions farther than our Solar System, taking spectators billions of light-years from Earth to the edge of the cosmos, while touch screens allow them to investigate the diverse and beautiful objects of deep space.',
      'Over the course of a year we built multiple interactive installations and projection systems to create a fully immersive and educational experience for visitors.',
      'Agency: M1 Interactive (now Bridgewater Studio)',
      'Visual Design: Max Glascott',
      'Lead Developer: Jason Araujo',
      'Built using Unity3D, Processing, Flash, Kinect, and Arduino',
    ],
    thumbnail: 'thumb.jpg',
    media: [
      {
        kind: 'local-image',
        filename: 'adler_01.jpg',
        alt: 'Adler Planetarium: The Universe: A Walk Through Space and Time',
        width: 1024,
        height: 540,
      },
      {
        kind: 'vimeo',
        id: '52340547',
        title: 'The Universe: A Walk Through Space and Time: video courtesy of M1 Interactive',
        aspectRatio: '16/9',
      },
    ],
    links: [
      { kind: 'client', label: 'Bridgewater Studio (formerly M1 Interactive)', url: 'https://www.bridgewaterstudio.net/' },
    ],
  },
  // {
  //   slug: 'placeholder-beta',
  //   title: '__PROJECT_BETA_TITLE__',
  //   client: 'test client',
  //   year: 2023,
  //   role: '__ROLE__',
  //   summary: '__PROJECT_BETA_SUMMARY__',
  //   media: [
  //     {
  //       kind: 'local-video',
  //       filename: 'demo.mp4',
  //       alt: '__PROJECT_BETA_TITLE__ — demo video',
  //       poster: 'poster.png',
  //       width: 1920,
  //       height: 1080,
  //     },
  //     {
  //       kind: 'vimeo',
  //       id: '__VIMEO_ID__',
  //       title: '__PROJECT_BETA_TITLE__ — walkthrough',
  //       aspectRatio: '16/9',
  //     },
  //     {
  //       kind: 'external-link',
  //       url: 'https://example.com/__PROJECT_BETA__',
  //       title: '__PROJECT_BETA_TITLE__ — live site',
  //       thumbnail: 'thumb.png',
  //     },
  //   ],
  //   links: [
  //     { kind: 'write-up', url: 'https://example.com/writing/__PROJECT_BETA__' },
  //   ],
  // },
];
